/**
 * Module 'attachments': screenshots, images and files on backlog items, missions and comments,
 * and on what a person sends a session: a message, or the note on an approval answer.
 *
 * Blobs live on the hub, content-addressed by sha256 (one copy however often they are
 * attached). An attachment record points at a blob and at what it is attached to. The type of
 * a file is decided by its bytes (sniffAttachment), never by its name or the browser's word:
 * only plain raster images and plain text are ever shown inline, everything else downloads.
 *
 * When a mission starts, the hub copies its attachments to the machine over the agent
 * connection (chunked, sha256-checked) into <agent home>/attachments/<mission>/ and lists their
 * absolute paths in the prompt, so the session can read them. Browser-safe and pure.
 */
import type { Id, Ts } from '../index.ts';
import { isRegexSpace, trimBothOf } from './trim.ts';

export const ATTACHMENTS_EXT = 'attachments';

/**
 * What an attachment can belong to. `null` target: uploaded, not attached yet (a draft).
 * 'session': sent with a message into a session; 'approval': sent with the note on an answer.
 */
export type AttachmentTargetKind = 'item' | 'mission' | 'comment' | 'session' | 'approval';
export const ATTACHMENT_TARGET_KINDS: readonly AttachmentTargetKind[] = ['item', 'mission', 'comment', 'session', 'approval'];

/** At most this many files go with one message or one approval note. */
export const ATTACHMENTS_PER_MESSAGE = 10;

export interface AttachmentTarget {
  kind: AttachmentTargetKind;
  id: Id;
}

/** How the console shows a file. */
export type AttachmentKind = 'image' | 'text' | 'pdf' | 'archive' | 'markup' | 'file';

export interface Attachment {
  /** `att_` and 22 random characters: not guessable, not time-ordered. */
  id: Id;
  sha256: string;
  /** The file name as uploaded, made safe (no folders, no control characters). */
  name: string;
  /** The type its bytes say it is (sniffAttachment), not what the uploader claimed. */
  mime: string;
  kind: AttachmentKind;
  size: number;
  /** Pixel size, for images whose header says it. */
  width?: number;
  height?: number;
  /** A small PNG or JPEG preview exists (GET /api/attachments/:id/thumb). */
  thumb: boolean;
  /** Who uploaded it (the actor id the audit log shows). */
  by: string;
  createdAt: Ts;
  target: AttachmentTarget | null;
}

/** state.ext.attachments */
export interface AttachmentsState {
  attachments: Attachment[];
  usage: AttachmentUsage;
}

export interface AttachmentUsage {
  /** Bytes of distinct blobs stored on the hub. */
  bytes: number;
  /** The hub's limit for all blobs together. */
  quota: number;
  count: number;
  /** Largest single file the hub accepts. */
  maxFile: number;
}

/** PATCH /api/attachments/:id */
export interface AttachmentPatch {
  /** Attach it (or move it) to an item, mission or comment. */
  target?: AttachmentTarget;
  name?: string;
}

/** Audit labels for this module's own actions. */
export const ATTACHMENTS_AUDIT_LABEL: Record<string, string> = {
  'attachment.upload': 'Upload attachment',
  'attachment.attach': 'Attach file',
  'attachment.rename': 'Rename attachment',
  'attachment.delete': 'Delete attachment',
  'attachment.deliver': 'Send attachments to machine',
  'attachment.gc': 'Clean up attachments',
};

// ---------------------------------------------------------------------------
// Limits
// ---------------------------------------------------------------------------

export const ATTACHMENT_MAX_BYTES = 25 * 1024 * 1024;
export const ATTACHMENT_QUOTA_BYTES = 2 * 1024 * 1024 * 1024;
export const ATTACHMENT_NAME_MAX = 120;
/** At most this many attachments on one item, mission or comment. */
export const ATTACHMENTS_PER_TARGET = 50;
/** A thumbnail upload may be at most this large. */
export const THUMB_MAX_BYTES = 256 * 1024;
/** Longest edge of a thumbnail. */
export const THUMB_EDGE = 320;
/** Raw bytes per delivery chunk (base64 on the wire, about 1.33 times larger). */
export const DELIVERY_CHUNK_BYTES = 192 * 1024;
/** An upload that was never attached is removed after this long. */
export const DRAFT_TTL_MS = 24 * 60 * 60_000;

const ID_RE = /^att_[A-Za-z0-9_-]{16,64}$/;
export function isAttachmentId(v: unknown): v is Id {
  return typeof v === 'string' && ID_RE.test(v);
}

export function isSha256(v: unknown): v is string {
  return typeof v === 'string' && /^[0-9a-f]{64}$/.test(v);
}

// ---------------------------------------------------------------------------
// Types from bytes
// ---------------------------------------------------------------------------

/** Types shown inline in the browser. Never SVG, HTML, XML or PDF. */
export const INLINE_SAFE_MIME: readonly string[] = ['image/png', 'image/jpeg', 'image/gif', 'image/webp', 'text/plain'];

export function isInlineSafe(mime: string): boolean {
  return INLINE_SAFE_MIME.includes(mime);
}

export interface Sniffed {
  mime: string;
  kind: AttachmentKind;
  /** The extension that matches the bytes, without the dot; '' when any name will do. */
  ext: string;
}

const startsWith = (b: Uint8Array, sig: readonly number[], at = 0) => sig.every((x, i) => b[at + i] === x);
const ascii = (b: Uint8Array, from: number, to: number) => String.fromCharCode(...b.subarray(from, Math.min(to, b.length)));

/** Markup a browser could run as a page: anything that looks like HTML, SVG or XML. */
const MARKUP_RE = /<\s*(!doctype|html|head|body|script|svg|iframe|object|embed|meta|link|style|xml|\?xml|math|foreignobject|img|a\s|form|base)\b/i;

/**
 * The type of a file from its first bytes (pass at least the first 8 KB). Text that looks like
 * markup is 'markup' and always downloads; text that is not valid UTF-8 is a plain file.
 */
export function sniffAttachment(head: Uint8Array): Sniffed {
  const b = head;
  if (startsWith(b, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a])) return { mime: 'image/png', kind: 'image', ext: 'png' };
  if (startsWith(b, [0xff, 0xd8, 0xff])) return { mime: 'image/jpeg', kind: 'image', ext: 'jpg' };
  if (ascii(b, 0, 6) === 'GIF87a' || ascii(b, 0, 6) === 'GIF89a') return { mime: 'image/gif', kind: 'image', ext: 'gif' };
  if (ascii(b, 0, 4) === 'RIFF' && ascii(b, 8, 12) === 'WEBP') return { mime: 'image/webp', kind: 'image', ext: 'webp' };
  if (ascii(b, 0, 5) === '%PDF-') return { mime: 'application/pdf', kind: 'pdf', ext: 'pdf' };
  if (startsWith(b, [0x50, 0x4b, 0x03, 0x04]) || startsWith(b, [0x50, 0x4b, 0x05, 0x06])) return { mime: 'application/zip', kind: 'archive', ext: '' };
  if (startsWith(b, [0x1f, 0x8b])) return { mime: 'application/gzip', kind: 'archive', ext: '' };
  if (!b.length) return { mime: 'text/plain', kind: 'text', ext: '' };
  if (b.includes(0)) return { mime: 'application/octet-stream', kind: 'file', ext: '' };
  if (!isUtf8(b)) return { mime: 'application/octet-stream', kind: 'file', ext: '' };
  // Markup tags are ASCII, so a byte-per-character view is enough to look for them.
  let text = '';
  for (let i = 0; i < b.length; i += 4096) text += String.fromCharCode(...b.subarray(i, i + 4096));
  // Control characters other than tab, newline, carriage return and form feed: not text.
  if (/[\u0001-\u0008\u000b\u000e-\u001f\u007f]/.test(text)) return { mime: 'application/octet-stream', kind: 'file', ext: '' };
  if (MARKUP_RE.test(text) || /^\s*</.test(text.replace(/^\uFEFF/, ''))) return { mime: 'application/octet-stream', kind: 'markup', ext: '' };
  return { mime: 'text/plain', kind: 'text', ext: '' };
}

/**
 * Valid UTF-8 (no overlongs, no surrogates). A sample may end in the middle of a character,
 * so an incomplete sequence at the very end is accepted.
 */
export function isUtf8(b: Uint8Array): boolean {
  let i = 0;
  while (i < b.length) {
    const c = b[i]!;
    if (c < 0x80) {
      i++;
      continue;
    }
    let n: number;
    let min: number;
    if (c >= 0xc2 && c <= 0xdf) (n = 1), (min = 0x80);
    else if (c >= 0xe0 && c <= 0xef) (n = 2), (min = 0x800);
    else if (c >= 0xf0 && c <= 0xf4) (n = 3), (min = 0x10000);
    else return false;
    if (i + n >= b.length) {
      // Cut off at the end of the sample: the bytes that are there must be continuations.
      for (let k = 1; i + k < b.length; k++) if ((b[i + k]! & 0xc0) !== 0x80) return false;
      return true;
    }
    let cp = c & (0x3f >> n);
    for (let k = 1; k <= n; k++) {
      const x = b[i + k]!;
      if ((x & 0xc0) !== 0x80) return false;
      cp = (cp << 6) | (x & 0x3f);
    }
    if (cp < min || cp > 0x10ffff || (cp >= 0xd800 && cp <= 0xdfff)) return false;
    i += n + 1;
  }
  return true;
}

/** Pixel size from an image header: PNG, GIF, WebP and JPEG. */
export function imageSize(b: Uint8Array): { width: number; height: number } | null {
  const u16be = (i: number) => ((b[i] ?? 0) << 8) | (b[i + 1] ?? 0);
  const u16le = (i: number) => (b[i] ?? 0) | ((b[i + 1] ?? 0) << 8);
  const u24le = (i: number) => (b[i] ?? 0) | ((b[i + 1] ?? 0) << 8) | ((b[i + 2] ?? 0) << 16);
  const u32be = (i: number) => (((b[i] ?? 0) << 24) >>> 0) + (((b[i + 1] ?? 0) << 16) | ((b[i + 2] ?? 0) << 8) | (b[i + 3] ?? 0));
  const ok = (w: number, h: number) => (w > 0 && h > 0 ? { width: w, height: h } : null);
  const s = sniffAttachment(b.subarray(0, 64));
  if (s.mime === 'image/png' && ascii(b, 12, 16) === 'IHDR') return ok(u32be(16), u32be(20));
  if (s.mime === 'image/gif') return ok(u16le(6), u16le(8));
  if (s.mime === 'image/webp') {
    const chunk = ascii(b, 12, 16);
    if (chunk === 'VP8 ') return ok(u16le(26) & 0x3fff, u16le(28) & 0x3fff);
    if (chunk === 'VP8L') {
      const bits = (b[21] ?? 0) | ((b[22] ?? 0) << 8) | ((b[23] ?? 0) << 16) | ((b[24] ?? 0) << 24);
      return ok((bits & 0x3fff) + 1, ((bits >>> 14) & 0x3fff) + 1);
    }
    if (chunk === 'VP8X') return ok(u24le(24) + 1, u24le(27) + 1);
    return null;
  }
  if (s.mime === 'image/jpeg') {
    let i = 2;
    while (i + 9 < b.length) {
      if (b[i] !== 0xff) return null;
      const marker = b[i + 1] ?? 0;
      if (marker === 0xd8 || marker === 0x01 || (marker >= 0xd0 && marker <= 0xd7)) {
        i += 2;
        continue;
      }
      const len = u16be(i + 2);
      // Start of frame markers (not DHT C4, JPG C8, DAC CC).
      if (marker >= 0xc0 && marker <= 0xcf && marker !== 0xc4 && marker !== 0xc8 && marker !== 0xcc) return ok(u16be(i + 7), u16be(i + 5));
      if (len < 2) return null;
      i += 2 + len;
    }
    return null;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Names
// ---------------------------------------------------------------------------

const WINDOWS_RESERVED = /^(con|prn|aux|nul|com[0-9]|lpt[0-9])(\..*)?$/i;

/**
 * A file name safe to show, to send in a header and to write on any OS: the last path part
 * only, no control or reserved characters, no leading dots, at most ATTACHMENT_NAME_MAX
 * characters (the extension kept). Never empty.
 */
export function safeFileName(raw: unknown, fallback = 'file'): string {
  // Bounded first, so no pattern below ever sees more than a few hundred characters.
  let n = typeof raw === 'string' ? raw.slice(-1000) : '';
  n = n.split(/[\\/]/).pop() ?? '';
  n = n
    .normalize('NFC')
    .replace(/[\u0000-\u001f\u007f-\u009f\u200b-\u200f\u202a-\u202e\u2066-\u2069]/g, '')
    .replace(/[<>:"|?*]/g, '_')
    .replace(/\s+/g, ' ')
    .trim();
  // Leading and trailing dots and blanks: a loop, not /[.\s]+$/ (quadratic in V8).
  n = trimBothOf(n, (ch) => ch === '.' || isRegexSpace(ch));
  if (!n) n = fallback;
  if (WINDOWS_RESERVED.test(n)) n = `_${n}`;
  if (n.length > ATTACHMENT_NAME_MAX) {
    const dot = n.lastIndexOf('.');
    const ext = dot > 0 && n.length - dot <= 12 ? n.slice(dot) : '';
    n = n.slice(0, ATTACHMENT_NAME_MAX - ext.length) + ext;
  }
  return n;
}

/** The extension each sniffed image or document type should carry. */
export const EXT_FOR_MIME: Record<string, string> = { 'image/png': 'png', 'image/jpeg': 'jpg', 'image/gif': 'gif', 'image/webp': 'webp', 'application/pdf': 'pdf' };

/** The name with the extension the bytes call for (an image named .html gets .png). */
export function nameForType(name: string, s: Sniffed): string {
  if (!s.ext) return name;
  const dot = name.lastIndexOf('.');
  const ext = dot > 0 ? name.slice(dot + 1).toLowerCase() : '';
  const same = ext === s.ext || (s.ext === 'jpg' && ext === 'jpeg');
  if (same) return name;
  return safeFileName(`${dot > 0 ? name.slice(0, dot) : name}.${s.ext}`);
}

/** RFC 6266 Content-Disposition with an ASCII fallback and the UTF-8 name. */
export function contentDisposition(kind: 'inline' | 'attachment', name: string): string {
  const fallback = name.replace(/[^\x20-\x7e]/g, '_').replace(/["\\%;]/g, '_') || 'file';
  const star = encodeURIComponent(name).replace(/['()*]/g, (c) => `%${c.charCodeAt(0).toString(16).toUpperCase()}`);
  return `${kind}; filename="${fallback}"; filename*=UTF-8''${star}`;
}

// ---------------------------------------------------------------------------
// Targets and references
// ---------------------------------------------------------------------------

/** 'item:itm_01...' */
export function targetKey(t: AttachmentTarget): string {
  return `${t.kind}:${t.id}`;
}

export function parseTarget(v: unknown): AttachmentTarget | null {
  if (typeof v === 'string') {
    const i = v.indexOf(':');
    if (i <= 0) return null;
    return parseTarget({ kind: v.slice(0, i), id: v.slice(i + 1) });
  }
  if (!v || typeof v !== 'object') return null;
  const o = v as { kind?: unknown; id?: unknown };
  if (!ATTACHMENT_TARGET_KINDS.includes(o.kind as AttachmentTargetKind)) return null;
  if (typeof o.id !== 'string' || !/^[A-Za-z0-9_-]{1,80}$/.test(o.id)) return null;
  return { kind: o.kind as AttachmentTargetKind, id: o.id };
}

/** Markdown refers to an attachment as attachment:<id>, for example ![shot](attachment:att_...). */
export const ATTACHMENT_SCHEME = 'attachment:';

export function attachmentRef(id: Id): string {
  return `${ATTACHMENT_SCHEME}${id}`;
}

/** The attachment id in an attachment: link, or null. */
export function parseAttachmentRef(href: string): Id | null {
  if (!href.startsWith(ATTACHMENT_SCHEME)) return null;
  const id = href.slice(ATTACHMENT_SCHEME.length);
  return isAttachmentId(id) ? id : null;
}

/** Every attachment id a Markdown text refers to, in order, without repeats. */
export function attachmentRefsIn(markdown: string): Id[] {
  const out: Id[] = [];
  for (const m of markdown.matchAll(/attachment:(att_[A-Za-z0-9_-]{16,64})/g)) if (!out.includes(m[1]!)) out.push(m[1]!);
  return out;
}

export function attachmentUrl(id: Id, variant: 'raw' | 'thumb' | 'download' = 'raw'): string {
  const base = `/api/attachments/${encodeURIComponent(id)}`;
  return variant === 'thumb' ? `${base}/thumb` : variant === 'download' ? `${base}/raw?download=1` : `${base}/raw`;
}

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(n < 10 * 1024 ? 1 : 0)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(n < 10 * 1024 * 1024 ? 1 : 0)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(1)} GB`;
}

// ---------------------------------------------------------------------------
// Delivery to a machine (hub <-> agent, ext 'attachments')
// ---------------------------------------------------------------------------

/**
 *   hub -> agent  offer      { xfer, folder, files: DeliveryFile[] }
 *   agent -> hub  offer.ok   { xfer, need: fileId[], have: { fileId: path } } | { xfer, error }
 *   hub -> agent  chunk      { xfer, fileId, seq, data (base64), last }
 *   agent -> hub  file.done  { xfer, fileId, ok, path?, error? }
 * The agent writes to <home>/attachments/<folder>/<name>, checks size and sha256 before the
 * file appears under its name, and never writes anywhere else.
 */
export interface DeliveryFile {
  fileId: Id;
  name: string;
  sha256: string;
  size: number;
}

export interface DeliveryOffer {
  xfer: string;
  /** One folder per mission: its id. */
  folder: string;
  files: DeliveryFile[];
}

export interface DeliveryOfferResult {
  xfer: string;
  need?: Id[];
  have?: Record<Id, string>;
  error?: string;
}

export interface DeliveryChunk {
  xfer: string;
  fileId: Id;
  seq: number;
  data: string;
  last: boolean;
}

export interface DeliveryFileDone {
  xfer: string;
  fileId: Id;
  ok: boolean;
  path?: string;
  error?: string;
}

/** A folder name for one delivery: letters, digits, _ and - only. */
export function isDeliveryFolder(v: unknown): v is string {
  return typeof v === 'string' && /^[A-Za-z0-9_-]{1,80}$/.test(v);
}

/** The delivery folder for files sent to one session (messages) or with one approval answer. */
export function deliveryFolderFor(kind: 'session' | 'approval', id: Id): string {
  return `${kind}-${id.replace(/[^A-Za-z0-9_-]/g, '_')}`.slice(0, 80);
}

/** attachmentIds from a request body: a list of attachment ids, or an error sentence. */
export function attachmentIdsOf(v: unknown, max = ATTACHMENTS_PER_MESSAGE): { ids: Id[] } | { error: string } {
  if (v === undefined || v === null) return { ids: [] };
  if (!Array.isArray(v) || !v.every(isAttachmentId)) return { error: 'attachmentIds must be a list of attachment ids.' };
  const ids = [...new Set(v as Id[])];
  if (ids.length > max) return { error: `Send at most ${max} files at a time.` };
  return { ids };
}

/** What a mission start delivered, for the prompt and the launch record. */
export interface AttachmentDelivery {
  ok: boolean;
  files: { id: Id; name: string; mime: string; size: number; path: string }[];
  failed: { id: Id; name: string; reason: string }[];
  message?: string;
}

/**
 * The text with each attachment:<id> reference the session can use pointed at the file's path
 * on its machine (a session cannot open attachment: links). Unknown ids are left as they are.
 */
export function resolveAttachmentRefs(text: string, paths: ReadonlyMap<Id, string>): string {
  if (!paths.size) return text;
  return text.replace(/attachment:(att_[A-Za-z0-9_-]{16,64})/g, (all, id: string) => {
    const p = paths.get(id);
    // Angle brackets keep a path with spaces or parentheses one Markdown link target.
    return p ? `<${p}>` : all;
  });
}

/**
 * The prompt section listing delivered files by absolute path (empty when there is nothing).
 * `from` says where they came from (default: a mission's item, the mission and their comments).
 */
export function attachmentPromptSection(d: Pick<AttachmentDelivery, 'files' | 'failed'>, from = 'the item, the mission and their comments'): string {
  if (!d.files.length && !d.failed.length) return '';
  const lines: string[] = [];
  if (d.files.length) {
    lines.push(`Attachments (screenshots and files from ${from}). Read them with the Read tool; images can be viewed directly:`);
    for (const f of d.files) lines.push(`- ${f.path} (${f.name}, ${f.mime}, ${formatBytes(f.size)})`);
  }
  if (d.failed.length) {
    lines.push(`${d.files.length ? 'These' : 'Attachments that'} could not be copied to this machine: ${d.failed.map((f) => `${f.name} (${f.reason})`).join('; ')}.`);
  }
  return lines.join('\n');
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

export function attachmentPatchError(v: unknown): string | null {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return 'Send the change as a JSON object.';
  const o = v as Record<string, unknown>;
  if ('target' in o && o.target !== undefined && !parseTarget(o.target)) return 'Attach it to an item, a mission, a comment, a session message or an approval.';
  if ('name' in o && o.name !== undefined && (typeof o.name !== 'string' || !o.name.trim() || o.name.length > 400)) return 'Give the file a name.';
  if (!('target' in o) && !('name' in o)) return 'Nothing to change.';
  return null;
}
